import { Button, Progress, Tag } from "antd";
import type { AiModel } from "../model/ai-model";
import { generationModeLabels } from "../model/generation-mode";
import type { ModelPricingSummary } from "../model/model-catalog-query";

export function ModelCard({
  model,
  onUse,
  onOpen,
  pricingSummary,
}: {
  model: AiModel;
  onUse: () => void;
  onOpen?: () => void;
  pricingSummary?: ModelPricingSummary | undefined;
}) {
  const status =
    model.status === "active"
      ? { color: "success", label: "可用" }
      : model.status === "beta"
        ? { color: "processing", label: "Beta" }
        : { color: "warning", label: "维护中" };
  const metadata = [model.provider, model.version].filter(Boolean).join(" · ");
  const hasMetrics = model.speedScore > 0 || model.qualityScore > 0;
  return (
    <article
      className={`model-catalog-card accent-${model.accent}`}
      role={onOpen ? "button" : undefined}
      tabIndex={onOpen ? 0 : undefined}
      onClick={onOpen}
      onKeyDown={(event) => {
        if (!onOpen) return;
        if (event.key === "Enter" || event.key === " ") {
          event.preventDefault();
          onOpen();
        }
      }}
    >
      <header>
        <span>{model.name.slice(0, 1)}</span>
        <div>
          <h2>
            {model.name}
            {model.recommended && <Tag color="purple">推荐</Tag>}
          </h2>
          <p>{metadata || `${model.capabilities.length} 项生成能力`}</p>
        </div>
        <Tag color={status.color}>{status.label}</Tag>
      </header>
      <p className="model-card-description">
        {model.description ||
          "服务端已配置该模型，具体可选参数会随生成能力自动加载。"}
      </p>
      <div className="model-capabilities">
        {model.capabilities.map((capability) => (
          <Tag key={capability} bordered={false}>
            {generationModeLabels[capability]}
          </Tag>
        ))}
        {model.tags.slice(0, 4).map((tag) => (
          <Tag key={tag} bordered={false}>
            {tag}
          </Tag>
        ))}
      </div>
      {hasMetrics && (
        <div className="model-metrics">
          <label>
            生成速度{" "}
            <Progress
              percent={model.speedScore}
              size="small"
              showInfo={false}
            />
          </label>
          <label>
            输出质量{" "}
            <Progress
              percent={model.qualityScore}
              size="small"
              showInfo={false}
              strokeColor="#22d3ee"
            />
          </label>
        </div>
      )}
      <footer>
        <div className="model-card-price">
          {model.pricing.credits > 0 ? (
            <span>
              <b>{model.pricing.credits}</b> 积分 / 次
            </span>
          ) : pricingSummary?.points.length ? (
            <>
              <span className="model-card-price-values">
                {pricingSummary.points.map((point) => (
                  <em key={point}>
                    <b>{point}</b> 积分
                  </em>
                ))}
              </span>
              <small>
                {generationModeLabels[pricingSummary.mode]} · 查看价格
              </small>
            </>
          ) : (
            <span>点击查看模型价格</span>
          )}
        </div>
        <Button
          disabled={model.status === "maintenance"}
          onClick={(event) => {
            event.stopPropagation();
            onUse();
          }}
        >
          使用模型
        </Button>
      </footer>
    </article>
  );
}
